import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChange, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { Company } from '../../../../../models/company.model';
import { CompanyService } from '../../../../../services/company.service';
import { SilobagMapComponent } from '../../../../../ui/components/silobag-map/silobag-map.component';
import { MapPoint } from '../../../../../ui/models/map-point.model';
import { sortBags } from '../../../silo-monitoring/utilities/silo-monitoring.stats';
import { SILOREAL_APP_URL, SiloBag, SiloBagRef } from '../../models/silo-bag.model';
import { SiloBagService } from '../../services/silo-bag.service';
import { SILO_BAG_COVERAGE_MARGIN, SILO_BAG_OVER_COVERAGE_MARGIN } from './silo-bag-selector.validators';

@Component({
  selector: 'app-silo-bag-selector',
  templateUrl: './silo-bag-selector.component.html',
  styleUrls: ['./silo-bag-selector.component.scss']
})
export class SiloBagSelectorComponent implements OnInit, OnChanges, OnDestroy {

  /** Tonelaje requerido por la orden ODD. */
  @Input() public orderQuantity: number = 0;
  /** Producto de la orden: el backend lista sólo las bolsas de ese cultivo. */
  @Input() public productId?: number;
  /** Cuando es true, deshabilita los checkboxes. */
  @Input() public disabled: boolean = false;
  /** Solo lectura: muestra las bolsas ya comprometidas (sin edición). */
  @Input() public readonly: boolean = false;
  /** SiloIDs preseleccionados (modo edición). */
  @Input() public committedIds: string[] = [];
  /**
   * SiloIDs de un borrador aún no persistido (ej. selección hecha en la vista de la orden,
   * recuperada al llegar a la pantalla de Nueva Contraorden). A diferencia de `committedIds`,
   * no implica que el backend ya tenga estas bolsas comprometidas: si alguna dejó de estar
   * disponible mientras tanto, simplemente no aparece preseleccionada.
   */
  @Input() public draftSelectedIds: string[] = [];
  /** Snapshot del backend (`negotiation.silo_bags`) en modo readonly: se ve sin llamar a Siloreal. */
  @Input() public committedSnapshot: SiloBagRef[] = [];
  /** True cuando las bolsas las comprometió la contraparte (ej. productor), no la empresa logueada. */
  @Input() public committedByCounterparty: boolean = false;
  /**
   * True cuando el negocio (Negotiation) que comprometió estas bolsas ya está cerrado
   * (status Booked, id 7). Habilita el link "Asegurar": el snapshot puede existir antes
   * de eso (se persiste en cada envío de contraoferta) sin que el negocio esté cerrado.
   */
  @Input() public isNegotiationClosed: boolean = false;
  /** En edición de contraorden: negociación a excluir del filtrado de comprometidas. */
  @Input() public excludeNegotiationId?: number;
  /**
   * Canje en edición: mantiene visibles las bolsas que el propio productor ya eligió en las
   * negociaciones hermanas del mismo canje (una por corredor, todas por el mismo grano).
   */
  @Input() public excludeBarterId?: number | string;
  /** Cuando está seteado, el listado es el stock del PRODUCTOR de esa negociación, no el de la empresa logueada. */
  @Input() public negotiationId?: number;
  /**
   * SiloIDs base del diff visual ("Nueva"/"Quitada"): lo comprometido hoy, no la propuesta
   * anterior (en el pre-anote no existe). Vacío = sin diff.
   */
  @Input() public baselineIds: string[] = [];
  /** Ofrece el check "silobolsas negociables". Sólo aplica al dueño del grano en edición (ver `showsNegotiableToggle`). */
  @Input() public showNegotiableToggle: boolean = false;
  /** Valor del check: true = la contraparte puede ver el stock disponible y proponer otras bolsas. */
  @Input() public negotiable: boolean = true;
  /** Readonly: el productor marcó las bolsas como no negociables y quien mira es la contraparte. */
  @Input() public lockedByProducer: boolean = false;

  /** Emite el array de bolsas seleccionadas. */
  @Output() public selectionChange = new EventEmitter<SiloBag[]>();
  /** Emite true cuando la cobertura es válida (suma > requerido * 1.10). */
  @Output() public validityChange = new EventEmitter<boolean>();
  /** Emite el nuevo valor del check "silobolsas negociables". */
  @Output() public negotiableChange = new EventEmitter<boolean>();

  public bags: SiloBag[] = [];
  public selected: { [siloId: string]: boolean } = {};
  public loading: boolean = true;
  public loadError: boolean = false;
  /** True cuando el backend respondió 400: la empresa no tiene credenciales de Siloreal. */
  public missingCredentials: boolean = false;
  public readonly margin = SILO_BAG_COVERAGE_MARGIN;
  public currentPage: number = 1;
  public readonly pageSize: number = 10;
  /** Orden vigente de la tabla (convención `campo` / `-campo`, igual que Monitoreo). Por defecto, peso embolsado ascendente. */
  public currentSort: string | undefined = 'bagged_weight';
  /**
   * SiloIDs que la tabla muestra al principio: foto de la selección tomada al preseleccionar,
   * ordenar o buscar. No se recalcula en cada toggle para que la fila no salte bajo el cursor.
   */
  private pinnedIds: Set<string> = new Set();

  /** Estado de seguro (`insured`) por SiloID, pedido en vivo (ver `showInsuredColumn`). */
  public insuredStatus: Map<string, boolean> = new Map();
  public insuredStatusLoading: boolean = false;
  public insuredStatusError: boolean = false;

  public viewMode: 'list' | 'map' = 'list';
  /** El mapa se crea al entrar por primera vez a la vista mapa; después sólo se oculta. */
  public mapActivated: boolean = false;
  /** Campo y no getter: `<silobag-map>` re-renderiza ante cada referencia nueva de `points`. */
  public mapPoints: MapPoint[] = [];
  public bagsWithoutGeo: number = 0;
  private mapPointsDirty: boolean = true;
  @ViewChild(SilobagMapComponent) private mapComponent?: SilobagMapComponent;

  private _searchTerm: string = '';
  public get searchTerm(): string { return this._searchTerm; }
  public set searchTerm(value: string) {
    this._searchTerm = value;
    this.currentPage = 1;
    this.pinSelected();
    this.rebuildMapPoints();
  }
  public readonly silorealAppUrl = SILOREAL_APP_URL;

  private company: Company;
  private destroy$ = new Subject<void>();
  private wasValid: boolean = false;
  private wasExcess: boolean = false;

  constructor(
    private companyService: CompanyService,
    private siloBagService: SiloBagService,
    private toastrService: ToastrService,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    // En modo readonly las bolsas comprometidas vienen del backend (snapshot persistido):
    // no se llama a Siloreal ni se necesita el listado de la empresa.
    if (this.readonly) {
      this.bags = this.bagsFromSnapshot();
      this.loading = false;
      this.rebuildMapPoints();
      this.emitValidity();
    }

    // La suscripción corre en ambos modos por simetría y para que `this.company` esté
    // disponible si en el futuro readonly necesita el companyId. Hoy se usa para
    // `loadBags()` (sólo en edición) y para el estado de seguro (sólo en readonly).
    this.companyService.watch().pipe(takeUntil(this.destroy$)).subscribe(company => {
      if (!company) return;
      this.company = company;
      if (!this.readonly) this.loadBags();
      if (this.showInsuredColumn && !this.insuredStatusLoading && this.insuredStatus.size === 0) {
        this.loadInsuredStatus();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['committedSnapshot'] && this.readonly) {
      this.bags = this.bagsFromSnapshot();
      this.rebuildMapPoints();
      this.emitValidity();
    }
    // Los padres bindean getters que rearman el array en cada ciclo: se compara por contenido
    // para no pisar la selección del usuario con la comprometida en cada detección de cambios.
    if (changes['committedIds'] && this.bags.length && this.idsChanged(changes['committedIds'])) {
      this.applyCommittedSelection();
      // loadBags() ya valida después de preseleccionar; acá no hay otro que lo haga.
      this.emitValidity();
    }
    if (changes['draftSelectedIds'] && this.bags.length && this.idsChanged(changes['draftSelectedIds'])) {
      this.applyDraftSelection();
    }
    if ((changes['orderQuantity'] && !changes['orderQuantity'].firstChange) && this.bags.length) {
      // Recalcular la validez cuando cambia la cantidad requerida.
      this.emitValidity();
    }
    // `baselineIds` viene de un getter: se compara por contenido para no redibujar el mapa en cada ciclo.
    const baselineChanged = changes['baselineIds'] && this.idsChanged(changes['baselineIds']);
    if (baselineChanged && this.bags.length) {
      this.rebuildMapPoints();
    }
  }

  /** True cuando el binding cambio de contenido, no solo de referencia. */
  private idsChanged(change: SimpleChange): boolean {
    const previous: string[] = change.previousValue ?? [];
    const current: string[] = change.currentValue ?? [];
    if (change.firstChange) return current.length > 0;
    if (previous.length !== current.length) return true;
    return current.some((id, index) => id !== previous[index]);
  }

  /** Construye los SiloBag mostrados en modo readonly a partir del snapshot del backend. */
  private bagsFromSnapshot(): SiloBag[] {
    return (this.committedSnapshot ?? []).map(ref => {
      const baggedDate = ref.bagged_date ? ref.bagged_date.slice(0, 10) : null;
      return new SiloBag({
        silo_id:            ref.silobag_id,
        silobag_uuid:       ref.silobag_uuid ?? null,
        silobag_name:       ref.silobag_name ?? null,
        grain_type:         (ref.species ?? '').toString().toLowerCase(),
        harvest:            ref.harvest ?? null,
        organization:       ref.organization ?? null,
        input_weight:       this.refNumber(ref.input_weight),
        remaining_stock:    this.refNumber(ref.remaining_stock),
        // El snapshot guarda stock en kg (como Siloreal); la tabla muestra toneladas.
        bagged_weight:      (this.refNumber(ref.remaining_stock) ?? this.refNumber(ref.input_weight) ?? 0) / 1000,
        lot_name:           ref.lot_name ?? null,
        countryside_name:   ref.countryside_name ?? null,
        humidity:           this.refNumber(ref.humidity),
        bagged_date:        baggedDate,
        days_since_bagging: baggedDate ? this.daysSince(baggedDate) : 0,
        latitude:           this.refNumber(ref.latitude),
        longitude:          this.refNumber(ref.longitude),
      });
    });
  }

  private daysSince(dateStr: string): number {
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return 0;
    const ms = Date.now() - new Date(year, month - 1, day).getTime();
    return Math.floor(ms / 86_400_000);
  }

  private refNumber(raw: number | string | null | undefined): number | null {
    if (raw == null || raw === '') return null;
    const parsed = typeof raw === 'number' ? raw : parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadBags(): void {
    this.loading = true;
    this.loadError = false;
    this.missingCredentials = false;

    // Modo "stock ajeno": pide el listado contra el PRODUCTOR, no contra this.company.
    const source$ = this.negotiationId != null
      ? this.siloBagService.listForNegotiation(this.company.id, this.negotiationId, this.productId)
      : this.siloBagService.listByCompany(this.company.id, this.excludeNegotiationId, this.excludeBarterId, this.productId);

    source$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: bags => {
          this.bags = bags;
          this.applyCommittedSelection();
          this.applyDraftSelection();
          this.loading = false;
          this.rebuildMapPoints();
          this.emitValidity();
        },
        error: (err: HttpErrorResponse) => {
          this.loading = false;
          // 400 = la empresa no tiene credenciales de Siloreal cargadas: hay que
          // indicarle al usuario que las configure, no es un error de conexión.
          this.missingCredentials = err?.status === 400;
          this.loadError = !this.missingCredentials;
          this.validityChange.emit(false);
        }
      });
  }

  /** Ruta a la pantalla de integraciones donde se cargan las credenciales de Siloreal. */
  public get integrationsLink(): string {
    return this.company ? `/company/${this.company.id}/integrations` : '';
  }

  /** Bolsas que se muestran en la tabla según el modo. */
  public get displayBags(): SiloBag[] {
    if (this.readonly) {
      return this.committedBags;
    }
    return this.availableBags;
  }

  /** Bolsas filtradas por el término de búsqueda (nombre y lote). */
  public get filteredDisplayBags(): SiloBag[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return this.displayBags;
    return this.displayBags.filter(bag =>
      (bag.silobag_name ?? '').toLowerCase().includes(term) ||
      (bag.lot_name ?? '').toLowerCase().includes(term) ||
      (bag.silo_id ?? '').toLowerCase().includes(term)
    );
  }

  public get totalPages(): number {
    return Math.ceil(this.filteredDisplayBags.length / this.pageSize);
  }

  public get paginatedBags(): SiloBag[] {
    const start = (this.currentPage - 1) * this.pageSize;
    const sorted = sortBags(this.filteredDisplayBags, this.currentSort);
    const pinned = sorted.filter(bag => this.pinnedIds.has(bag.silo_id));
    const rest = sorted.filter(bag => !this.pinnedIds.has(bag.silo_id));
    return pinned.concat(rest).slice(start, start + this.pageSize);
  }

  /** Alterna asc/desc/sin-orden sobre `field`, igual convención que la tabla de Monitoreo. */
  public sort(field: string): void {
    this.currentSort = this.currentSort === field ? '-' + field : field;
    this.currentPage = 1;
    this.pinSelected();
  }

  /** Congela la selección actual como el bloque que va primero en la tabla (ver `pinnedIds`). */
  private pinSelected(): void {
    this.pinnedIds = new Set(Object.keys(this.selected).filter(id => this.selected[id]));
  }

  public sortIcon(field: string): string {
    if (this.currentSort === field) return 'arrow_upward';
    if (this.currentSort === '-' + field) return 'arrow_downward';
    return 'unfold_more';
  }

  public isActiveSort(field: string): boolean {
    return this.currentSort === field || this.currentSort === '-' + field;
  }

  public get pageNumbers(): number[] {
    const total = this.totalPages;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const delta = 2;
    const left = Math.max(2, this.currentPage - delta);
    const right = Math.min(total - 1, this.currentPage + delta);
    const pages: number[] = [1];
    if (left > 2) pages.push(-1);
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < total - 1) pages.push(-1);
    pages.push(total);
    return pages;
  }

  public goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
  }

  /** Comprometidas de esta negociación: en readonly `this.bags` ya es el snapshot; en edición filtra por `committedIds`. */
  public get committedBags(): SiloBag[] {
    if (this.readonly) return this.bags;
    if (!this.committedIds?.length) return [];
    const ids = new Set(this.committedIds);
    return this.bags.filter(bag => ids.has(bag.silo_id));
  }

  /** Bolsas disponibles para asignar (no comprometidas con otra orden). El cultivo ya lo filtra el backend. */
  public get availableBags(): SiloBag[] {
    return this.bags.filter(bag => bag.assigned_order_id == null);
  }

  public get selectedBags(): SiloBag[] {
    if (this.readonly) {
      return this.committedBags;
    }
    return this.availableBags.filter(bag => this.selected[bag.silo_id]);
  }

  public get selectedTotal(): number {
    return this.selectedBags.reduce((sum, b) => sum + (Number(b.bagged_weight) || 0), 0);
  }

  public get requiredCoverage(): number {
    return (Number(this.orderQuantity) || 0) * this.margin;
  }

  public get hasValidCoverage(): boolean {
    return this.selectedTotal > this.requiredCoverage && this.selectedBags.length > 0;
  }

  /** Tope a partir del cual se avisa sobre-cobertura (+30% sobre la cobertura mínima). */
  public get overCoverageLimit(): number {
    return this.requiredCoverage * SILO_BAG_OVER_COVERAGE_MARGIN;
  }

  /** Sólo informativo: no afecta `hasValidCoverage` ni la validez emitida. */
  public get hasExcessCoverage(): boolean {
    return this.requiredCoverage > 0 && this.selectedTotal > this.overCoverageLimit;
  }

  /** Sólo hay diff si hay algo comprometido: sin baseline, toda bolsa tildada sería "Nueva" y no aporta. */
  public get showsDiff(): boolean {
    return !this.readonly && (this.baselineIds?.length ?? 0) > 0;
  }

  /** Bolsa tildada que NO estaba en el turno anterior. */
  public isNewlyProposed(bag: SiloBag): boolean {
    return this.showsDiff && !!this.selected[bag.silo_id] && !this.baselineIds.includes(bag.silo_id);
  }

  /** Bolsa del turno anterior que ya NO está tildada. */
  public isRemovedFromBaseline(bag: SiloBag): boolean {
    return this.showsDiff && !this.selected[bag.silo_id] && this.baselineIds.includes(bag.silo_id);
  }

  /** Cuántas bolsas se mantienen/agregaron/quitaron respecto del baseline. `null` = sin cambios (o sin diff). */
  public get siloBagChanges(): { kept: number; added: number; removed: number } | null {
    if (!this.showsDiff) return null;

    const baseline = new Set(this.baselineIds);
    const current = new Set(this.selectedBags.map(bag => bag.silo_id));

    let added = 0;
    current.forEach(id => { if (!baseline.has(id)) added++; });
    let removed = 0;
    baseline.forEach(id => { if (!current.has(id)) removed++; });

    return (added > 0 || removed > 0) ? { kept: current.size - added, added, removed } : null;
  }

  public get committedHelpKey(): string {
    return this.committedByCounterparty
      ? 'SILO_BAGS.COMMITTED_HELP_COUNTERPARTY'
      : 'SILO_BAGS.COMMITTED_HELP';
  }

  /** El check sólo tiene sentido sobre stock propio en edición: en modo "stock ajeno" quien mira no es el productor. */
  public get showsNegotiableToggle(): boolean {
    return this.showNegotiableToggle && !this.readonly && this.negotiationId == null;
  }

  public toggleNegotiable(): void {
    if (this.disabled) return;
    this.negotiable = !this.negotiable;
    this.negotiableChange.emit(this.negotiable);
  }

  /** Clave i18n del aviso "no negociables" en modo readonly; `null` si no corresponde mostrarlo. */
  public get lockedNoticeKey(): string | null {
    if (!this.readonly) return null;
    if (this.lockedByProducer) return 'SILO_BAGS.LOCKED_COUNTERPARTY';
    return this.negotiable === false && !this.committedByCounterparty ? 'SILO_BAGS.LOCKED_OWN' : null;
  }

  private applyCommittedSelection(): void {
    this.selected = {};
    (this.committedIds ?? []).forEach(id => {
      this.selected[id] = true;
    });
    this.pinSelected();

    // Se emite siempre (incluso vacío) para que el padre no envíe una selección vieja
    // ni pierda la preselección si se envía sin tocar el selector.
    this.selectionChange.emit(this.selectedBags);
    this.rebuildMapPoints();
  }

  /**
   * Preselecciona un borrador (`draftSelectedIds`) sólo contra bolsas que siguen disponibles
   * (`availableBags`). Si una bolsa del borrador fue comprometida por otra negociación mientras
   * tanto, se ignora en vez de marcarla seleccionada-pero-invisible: `selectedTotal` y la tabla
   * quedarían inconsistentes entre sí si se marcara igual.
   */
  private applyDraftSelection(): void {
    if (!this.draftSelectedIds?.length || this.committedIds?.length) return;
    const availableIds = new Set(this.availableBags.map(bag => bag.silo_id));
    const restoredIds = this.draftSelectedIds.filter(id => availableIds.has(id));
    restoredIds.forEach(id => { this.selected[id] = true; });
    this.pinSelected();

    // Feedback explícito: sin esto, los checkboxes aparecen tildados sin que el usuario
    // entienda si fue él o el sistema, y si algo quedó afuera no hay forma de distinguirlo
    // del simple "todavía no seleccionaste suficiente" (mismo mensaje COVERAGE_KO).
    if (restoredIds.length) {
      this.toastrService.info(this.translateService.instant('SILO_BAGS.DRAFT_RESTORED'));
    }
    const unavailableCount = this.draftSelectedIds.length - restoredIds.length;
    if (unavailableCount > 0) {
      this.toastrService.warning(this.translateService.instant('SILO_BAGS.DRAFT_BAGS_UNAVAILABLE', { count: unavailableCount }));
    }

    this.selectionChange.emit(this.selectedBags);
    this.rebuildMapPoints();
    this.emitValidity();
  }

  public toggle(bag: SiloBag): void {
    if (this.disabled || this.readonly) return;
    this.selected[bag.silo_id] = !this.selected[bag.silo_id];
    this.syncMapVariant(bag);
    this.selectionChange.emit(this.selectedBags);
    this.emitValidity(true);
  }

  public setViewMode(mode: 'list' | 'map'): void {
    if (this.viewMode === mode) return;
    this.viewMode = mode;
    if (mode === 'map') {
      this.mapActivated = true;
      if (this.mapPointsDirty) this.rebuildMapPoints();
    }
  }

  public onMapSelect(siloId: string): void {
    const bag = this.filteredDisplayBags.find(b => b.silo_id === siloId);
    if (bag) this.toggle(bag);
  }

  public focusSelectedOnMap(bag: SiloBag): void {
    if (this.viewMode === 'map' && this.hasCoordinates(bag)) this.mapComponent?.focusPoint(bag.silo_id);
  }

  public removeSelected(bag: SiloBag, event: Event): void {
    event.stopPropagation();
    if (this.selected[bag.silo_id]) this.toggle(bag);
  }

  public focusOnMap(bag: SiloBag, event: Event): void {
    event.stopPropagation();
    if (!this.hasCoordinates(bag)) return;
    this.setViewMode('map');
    // En la primera activación el `@ViewChild` todavía no está resuelto.
    setTimeout(() => this.mapComponent?.focusPoint(bag.silo_id));
  }

  /** Con el mapa oculto sólo se marca como desactualizado: dibujarlo con alto 0 lo encuadra mal. */
  private rebuildMapPoints(): void {
    if (this.viewMode !== 'map') {
      this.mapPointsDirty = true;
      return;
    }
    const visible = this.filteredDisplayBags;
    const withGeo = visible.filter(bag => this.hasCoordinates(bag));
    this.bagsWithoutGeo = visible.length - withGeo.length;
    this.mapPoints = withGeo.map(bag => this.toMapPoint(bag));
    this.mapPointsDirty = false;
  }

  private toMapPoint(bag: SiloBag): MapPoint {
    return {
      id: bag.silo_id,
      lat: bag.latitude as number,
      lng: bag.longitude as number,
      title: bag.silobag_name || bag.silo_id,
      variant: this.mapVariantFor(bag),
      hover: {
        code: bag.silo_id,
        species: bag.grain_type,
        baggedDate: bag.bagged_date ?? null,
        lot: bag.lot_name,
        stockKg: bag.remaining_stock ?? (bag.bagged_weight != null ? bag.bagged_weight * 1000 : null),
        harvest: bag.harvest,
        humidity: bag.humidity,
        // Sin fecha el modelo deja 0 días.
        daysSinceBagging: bag.bagged_date ? bag.days_since_bagging : null
      }
    };
  }

  private mapVariantFor(bag: SiloBag): MapPoint['variant'] {
    if (this.readonly) return 'committed';
    if (this.selected[bag.silo_id]) return 'available';
    if (this.isRemovedFromBaseline(bag)) return 'alert';
    return 'muted';
  }

  /** Recolorea sólo el pin tocado para que la cámara no salte en cada click. */
  private syncMapVariant(bag: SiloBag): void {
    if (this.viewMode !== 'map') {
      this.mapPointsDirty = true;
      return;
    }
    this.mapComponent?.updatePointVariant(bag.silo_id, this.mapVariantFor(bag));
  }

  public baggedDateValue(bag: SiloBag): Date | null {
    if (!bag.bagged_date) return null;
    const [year, month, day] = bag.bagged_date.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }

  public hasCoordinates(bag: SiloBag): boolean {
    return bag.latitude != null && bag.longitude != null;
  }

  /**
   * Clave i18n del título. En modo "stock ajeno" las bolsas no son propias del comprador,
   * así que "a comprometer" (vereda del dueño del grano) lo confundiría.
   */
  public get titleKey(): string {
    if (this.readonly) return 'SILO_BAGS.COMMITTED_TITLE';
    return this.negotiationId != null ? 'SILO_BAGS.TITLE_COUNTERPARTY' : 'SILO_BAGS.TITLE';
  }

  /** Clave i18n de la ayuda: mismo criterio que `titleKey`. */
  public get helpKey(): string {
    return this.negotiationId != null ? 'SILO_BAGS.HELP_COUNTERPARTY' : 'SILO_BAGS.HELP';
  }

  /** Clave i18n del aviso sin stock: en modo "stock ajeno" no es "cargá tus credenciales" (son las del productor). */
  public get missingStockMessageKey(): string {
    return this.negotiationId != null
      ? 'SILO_BAGS.COUNTERPARTY_NO_STOCK'
      : 'SILO_BAGS.MISSING_CREDENTIALS';
  }

  /** True cuando corresponde ofrecer el link a "Cargar credenciales" (sólo cuenta propia). */
  public get showIntegrationsLink(): boolean {
    return this.negotiationId == null;
  }

  /**
   * ¿Se muestra la columna "Asegurado"? Mismo gate que el botón "Asegurar": recién tiene
   * sentido cuando el negocio que comprometió las bolsas ya está cerrado.
   */
  public get showInsuredColumn(): boolean {
    return this.readonly && this.isNegotiationClosed;
  }

  public isInsured(bag: SiloBag): boolean {
    return this.insuredStatus.get(bag.silo_id) === true;
  }

  /** Pide a Siloreal el estado de seguro de todas las bolsas de la empresa (ver `SiloBagService.listInsuredStatus`). */
  private loadInsuredStatus(): void {
    if (!this.company) return;
    this.insuredStatusLoading = true;
    this.insuredStatusError = false;
    this.siloBagService.listInsuredStatus(this.company.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: status => {
          this.insuredStatus = status;
          this.insuredStatusLoading = false;
        },
        error: () => {
          this.insuredStatusLoading = false;
          this.insuredStatusError = true;
        }
      });
  }

  /** `notify`: el toast de cobertura sólo sale por acción del usuario, no por la preselección. */
  private emitValidity(notify: boolean = false): void {
    if (this.readonly) {
      this.validityChange.emit(this.committedBags.length > 0);
      return;
    }
    const valid = this.hasValidCoverage;
    if (notify && valid && !this.wasValid) {
      this.toastrService.success(this.translateService.instant('SILO_BAGS.COVERAGE_OK'));
    }
    this.wasValid = valid;

    // Mismo criterio que COVERAGE_OK: sólo al cruzar el umbral, no en cada toggle ya excedido.
    const excess = this.hasExcessCoverage;
    if (notify && excess && !this.wasExcess) {
      this.toastrService.error(this.translateService.instant('SILO_BAGS.COVERAGE_EXCESS', {
        limit: this.overCoverageLimit.toLocaleString(this.translateService.currentLang, { maximumFractionDigits: 2 })
      }));
    }
    this.wasExcess = excess;

    this.validityChange.emit(valid);
  }
}
